import React from "react";
import {
  PolicyManagementRecord,
  BookedRowSource,
} from "entities/UIModel/PolicyManagementRecord";
import {
  OptionRenderType,
} from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import StewartAutocompleteWithCheckboxes from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import { AsyncTextFilterState } from "utils/custom-hooks/useAsyncTextFilter";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import MissingFieldWarning from "./MissingFieldWarning";

export function formatCurrency(value: number | null | undefined): string {
  if (value === undefined || value === null) return "";
  return (
    "$" +
    value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

export function formatRemitSplitPct(
  value: number | null | undefined
): string {
  if (value === undefined || value === null) return "";
  return (
    value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + "%"
  );
}

// Join non-empty TransCode + TransCode2..TransCode8 values with ", ".
// filter drops null/undefined/empty so commas never appear without a
// preceding value.
export function formatTransCodes(row: PolicyManagementRecord): string {
  const values: (string | null | undefined)[] = [
    row.TransCode,
    row.TransCode2,
    row.TransCode3,
    row.TransCode4,
    row.TransCode5,
    row.TransCode6,
    row.TransCode7,
    row.TransCode8,
  ];
  return values
    .filter((v): v is string => !!v && v.trim() !== "")
    .join(", ");
}

export function fieldErrorMessage(
  row: PolicyManagementRecord,
  fieldName: string
): string {
  const error = (row.PolicyErrors ?? []).find(
    (e) => e.ErrorFieldName?.toLowerCase() === fieldName.toLowerCase()
  );
  if (!error) return "";
  return error.PolicyErrorExceptionMessage || error.ErrorFieldName || "";
}

// false: Renders Warnings First.
// true: Renders Data First.
export const SHOW_VALUE_INSTEAD_OF_FIELD_WARNING = false;

export function fieldErrorCell(
  row: PolicyManagementRecord,
  fieldName: string,
  value: string,
  showValue: boolean = SHOW_VALUE_INSTEAD_OF_FIELD_WARNING
): React.ReactNode {
  const message = fieldErrorMessage(row, fieldName);
  if (!message) return <TooltipCell title={value} />;
  // TooltipCell renders if dat is missing.
  if (showValue && value?.trim()) return <TooltipCell title={value} />;
  return <MissingFieldWarning title={message} />;
}

export function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Local-date midnight with explicit time component, no timezone suffix.
// "June 16" → "2026-06-16T00:00:00".
export function toIsoDateMidnight(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T00:00:00`;
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

// Yes/No choices for the HOI column's single-select header filter. Values are
// 1/0 so the criteria binds to the backend's int parameter.
export const HOI_FILTER_OPTIONS = [
  { label: "Yes", value: 1 },
  { label: "No", value: 0 },
];

export interface BuildPolicyColumnsOptions {
  agentOptions?: OptionRenderType[];
  stateOptions?: OptionRenderType[];
  productTypeOptions?: OptionRenderType[];
  recordTypeOptions?: OptionRenderType[];
  propertyTypeOptions?: OptionRenderType[];
  countyOptions?: OptionRenderType[];
  formPrefixOptions?: OptionRenderType[];
  transCodeOptions?: OptionRenderType[];
  // Async typeahead bundles for text-search dropdowns. Each owns its own
  // debounced input handler + loading flag (see useAsyncTextFilter).
  policyFormNameAsync?: AsyncTextFilterState;
  fileNumberAsync?: AsyncTextFilterState;
  originalPolicyNumberAsync?: AsyncTextFilterState;
  closedByUserAsync?: AsyncTextFilterState;
  sourceOptions?: OptionRenderType[];
  // Async-mode bits for the AgentID column in QR. `onAgentInputChange` is the
  // debounced search handler the parent provides; `agentLoading` flips the
  // spinner inside the dropdown while the request is in flight.
  agentLoading?: boolean;
  onAgentInputChange?: (text: string) => void;
  // `number` is for single-select header filters whose criteria bind to a
  // backend int (e.g. IsHOI, 1/0) — serializeFilters passes those through unquoted.
  onFiltersChange?: (
    name: string,
    value: string | number | null
  ) => void;
  onMultiSelectChange?: (
    e: { target: { name: string; value: OptionRenderType[] } },
    value: OptionRenderType[]
  ) => void;
  onBookedToggle?: (record: PolicyManagementRecord, next: BookedRowSource) => void;
  onValidationClick?: (record: PolicyManagementRecord) => void;
  onRowAction?: (record: PolicyManagementRecord) => void;
  // Initial header search values keyed by filter name (Global Search seed).
  headerFilterDefaults?: Record<string, string>;
}

export const SafeAutocomplete = ({
  name,
  options,
  onChange,
  loading,
  onInputChange,
  showValueAsTag,
  filterOptionsLimit,
  popperMinWidth,
}: {
  name: string;
  options?: OptionRenderType[];
  onChange?: BuildPolicyColumnsOptions["onMultiSelectChange"];
  loading?: boolean;
  onInputChange?: (text: string) => void;
  showValueAsTag?: boolean;
  filterOptionsLimit?: number;
  popperMinWidth?: number;
}) => {
  // Async mode (onInputChange supplied) drives options from the parent, so
  // an empty initial list is fine. Static mode still requires options.
  if (!onChange) return null;
  if (!onInputChange && !options) return null;
  return (
    <StewartAutocompleteWithCheckboxes
      name={name}
      onChange={onChange}
      options={options ?? []}
      placeholder="Search"
      autoSelectSingleOption={false}
      loading={loading}
      onInputChange={onInputChange}
      showValueAsTag={showValueAsTag}
      filterOptionsLimit={filterOptionsLimit}
      popperMinWidth={popperMinWidth}
      enableClearable
    />
  );
};
